import React, { useEffect, useState } from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';
import { IntlProvider, FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { get, isEmpty, isEqual } from 'lodash';

import asyncRoute from '../../helpers/hoc/asyncRoute';
import { extractWhitelabelSubdomain } from '../../utils/helpers';
import rewardRoutes from '../../routes/rewards';
import ThemeContext from '../ThemeContext';
import DefaultRegularFont from '../../assets/fonts/OpenSans-Regular.ttf';
import DefaultSemiBoldFont from '../../assets/fonts/OpenSans-SemiBold.ttf';
import DefaultBoldFont from '../../assets/fonts/OpenSans-Bold.ttf';
import resourcesConfig from '../../resources';
import { useTheme } from '../../utils/customHooks';

import { apiResponseCodes, serverErrorCodes } from '../../constants/globals';
import {
  fetchAppStartupInfoAction,
  fetchBrandResourcesAction,
  dispatchStaticContext,
} from '../../actions/common';
import '../Rewards/index.scss';

const NonExistentSubdomainFallback = asyncRoute(() =>
  import('../Fallback/NonExistentSubdomainFallback'),
);
const Signup = asyncRoute(() => import('./Signup'));
const RewardDetails = asyncRoute(() => import('./RewardDetails'));

const Authentication = ({
  match,
  resources,
  actions,
  location,
  themeData,
  fontFamilyUrl,
  boldFontURL,
  semiBoldFontURL,
}) => {
  useTheme('rewards-container', themeData);

  const [showNonExistentSubdomainFallback, setShowNonExistentSubdomainFallback] = useState(false);

  useEffect(() => {
    const subDomain = extractWhitelabelSubdomain();
    if (subDomain) {
      const appStartupReqBody = {
        subdomain: subDomain,
        includeModules: ['reward', 'brand', 'resource'],
      };
      actions.dispatchStaticContext('instagram');
      fetchStartupInfoData({ appStartupReqBody });

      actions.fetchBrandResources({ brandId: subDomain }).then((response) => {
        if (
          !apiResponseCodes.SUCCESS.includes(get(response, 'status', '')) &&
          !isEqual(get(response, 'data.code'), serverErrorCodes.invalidToken)
        ) {
          // incase if sub domain is not resolved
          setShowNonExistentSubdomainFallback(true);
        }
      });
    }
  }, []);

  useEffect(() => {
    const defaultFontURL = DefaultRegularFont;
    const defaultFont = 'DefaultFont';
    const fontURL = !isEmpty(fontFamilyUrl) ? fontFamilyUrl : DefaultRegularFont;
    const RegularFont = 'RegularFont';
    const bFontURL = !isEmpty(boldFontURL) ? boldFontURL : DefaultBoldFont;
    const BoldFont = 'BoldFont';
    const sBFontURL = !isEmpty(semiBoldFontURL) ? semiBoldFontURL : DefaultSemiBoldFont;
    const SemiBoldFont = 'SemiBoldFont';
    var newStyle = document.createElement('style');
    newStyle.appendChild(
      document.createTextNode(
        '\
@font-face {\
    font-family: ' +
          RegularFont +
          ";\
    src: url('" +
          fontURL +
          "') format('woff');\
}\
",
      ),
    );
    newStyle.appendChild(
      document.createTextNode(
        '\
@font-face {\
  font-family: ' +
          BoldFont +
          ";\
  src: url('" +
          bFontURL +
          "') format('woff');\
}\
",
      ),
    );
    newStyle.appendChild(
      document.createTextNode(
        '\
@font-face {\
  font-family: ' +
          SemiBoldFont +
          ";\
  src: url('" +
          sBFontURL +
          "') format('woff');\
}\
",
      ),
    );
    newStyle.appendChild(
      document.createTextNode(
        '\
@font-face {\
  font-family: ' +
          defaultFont +
          ";\
  src: url('" +
          defaultFontURL +
          "') format('woff');\
}\
",
      ),
    );
    document.head.appendChild(newStyle);
  }, [fontFamilyUrl, boldFontURL, semiBoldFontURL]);

  const fetchStartupInfoData = ({ appStartupReqBody }) =>
    actions.fetchAppStartupInfo({ appStartupReqBody }).then((response) => {
      if (apiResponseCodes.SUCCESS.includes(get(response, 'status', ''))) {
      } else if (!isEqual(get(response, 'data.code'), serverErrorCodes.invalidToken)) {
        // incase if sub domain is not resolved
        setShowNonExistentSubdomainFallback(true);
      }
    });

  const handleNonExistentSubdomainClick = () => {
    window.location.reload();
  };

  const renderRoutes = () => (
    <IntlProvider messages={resources} locale="en-US" defaultLocale="en-US">
      {showNonExistentSubdomainFallback ? (
        <NonExistentSubdomainFallback
          resources={{
            title: (
              <FormattedMessage
                id="nonExistSubdomainFallbackTitle"
                defaultMessage={resourcesConfig.nonExistSubdomainFallbackTitle}
              />
            ),
            description: (
              <FormattedMessage
                id="nonExistSubdomainFallbackDescription"
                defaultMessage={resourcesConfig.nonExistSubdomainFallbackDescription}
              />
            ),
            atnBtn: (
              <FormattedMessage
                id="nonExistSubdomainFallbackCTA"
                defaultMessage={resourcesConfig.nonExistSubdomainFallbackCTA}
              />
            ),
          }}
          handleGoHome={() => handleNonExistentSubdomainClick()}
        />
      ) : (
        <Switch location={location}>
          <Route
            exact
            path={`${match.path}`}
            render={() => <Redirect to={`${match.path}/${rewardRoutes.authentication.signUp}`} />}
          />
          <Route path={`${match.path}/${rewardRoutes.authentication.signUp}`} component={Signup} />
          <Route
            path={`${match.path}/${rewardRoutes.containers.dealDetail}/:dealCode`}
            component={RewardDetails}
          />
        </Switch>
      )}
    </IntlProvider>
  );

  return (
    <ThemeContext.Provider value={{ themeContext: rewardRoutes.rewards }}>
      <div id="rewards-container" className="rewards">
        {renderRoutes()}
      </div>
    </ThemeContext.Provider>
  );
};

const mapStateToProps = (state) => ({
  resources: get(state, 'common.resources', resourcesConfig),
  themeData: get(state, 'common.resources.themeData', {}),
  fontFamilyUrl: get(state, 'common.resources.themeData.fontFamilyUrl', ''),
  semiBoldFontURL: get(state, 'common.resources.themeData.semiBoldFontURL', ''),
  boldFontURL: get(state, 'common.resources.themeData.boldFontURL', ''),
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      fetchAppStartupInfo: fetchAppStartupInfoAction,
      fetchBrandResources: fetchBrandResourcesAction,
      dispatchStaticContext: dispatchStaticContext,
    },
    dispatch,
  ),
});

export default connect(mapStateToProps, mapDispatchToProps)(Authentication);
